import { signOut } from "@/lib/sign-out";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { StorageUtility } from "@/lib/storage";
import { currentEnvironment } from "@/lib/utils";
import {
  ShieldAlert,
  LogOut,
  CreditCard,
  TrendingUp,
  FileCheck,
} from "lucide-react";
import { datadogRum } from "@datadog/browser-rum";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { CurrentEnvironment } from "@/components/current-environment";
import { SessionExpirationCheck } from "@/routes/auth/_auth-layout/-components/auth/session-expiration-check";
import {
  canAdminRead,
  getDefaultMerchantRoute,
  jwtIsExpired,
  parseVolumeJwt,
  redirectToSignIn,
} from "@/lib/auth";

const validateAdminSession = (token: string) => {
  if (jwtIsExpired(token)) {
    throw redirectToSignIn();
  }

  const tokenContents = parseVolumeJwt(token);
  const adminEmail = StorageUtility.getItem("v_adminEmail_v1");

  if (adminEmail) {
    datadogRum.setGlobalContextProperty("adminEmail", adminEmail);
  }

  if (!tokenContents.adminId && tokenContents.merchantId) {
    throw redirect({
      to: getDefaultMerchantRoute(tokenContents.authorities),
    });
  }

  if (
    !tokenContents.adminId ||
    tokenContents.merchantId ||
    !canAdminRead(tokenContents.authorities)
  ) {
    throw redirectToSignIn();
  }

  return {
    tokenContents,
    adminEmail,
  };
};

export const Route = createFileRoute("/(app)/_admin-layout")({
  component: RouteComponent,
  beforeLoad: async () => {
    datadogRum.setGlobalContextProperty("environment", currentEnvironment());

    const token = StorageUtility.getItem("v_accessToken_v1");
    if (!token) {
      throw redirectToSignIn();
    }

    const { tokenContents, adminEmail } = validateAdminSession(token);

    return {
      accessToken: token,
      decodedJwt: tokenContents,
      authorities: tokenContents.authorities,
      adminEmail: adminEmail ?? "",
      isAdmin: tokenContents.adminId !== undefined,
    };
  },
});

function AdminLayoutContent() {
  const context = Route.useRouteContext();
  const router = useRouter();
  const { state: sidebarState } = useSidebar();

  return (
    <>
      <Sidebar collapsible="icon">
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="!h-9 w-full justify-center group-data-[collapsible=icon]:opacity-100 group-data-[collapsible=icon]:-mt-0 group-data-[collapsible=icon]:!h-9">
              <div className="flex h-9 w-full items-center justify-center group-data-[collapsible=icon]:h-9">
                <img
                  src="/volume-logo.svg"
                  width={120}
                  height={32}
                  alt="Volume Logo"
                  className="group-data-[collapsible=icon]:hidden"
                />
                <span className="hidden size-7 min-w-7 min-h-7 shrink-0 overflow-hidden rounded-full group-data-[collapsible=icon]:inline-flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center">
                  <img
                    src="/favicon.ico"
                    width={28}
                    height={28}
                    alt="Volume"
                    className="size-full object-cover"
                  />
                </span>
              </div>
            </SidebarGroupLabel>
            <SidebarGroupContent className="mt-4">
              <div className="group-data-[collapsible=icon]:hidden">
                <CurrentEnvironment />
              </div>
              <div className="group-data-[collapsible=icon]:hidden mb-4 px-2 mt-3">
                <div className="flex flex-col gap-1 overflow-hidden border-b px-3 pb-4">
                  <p className="text-xs font-medium text-muted-foreground">
                    Admin workspace
                  </p>
                  <p className="truncate text-sm font-medium text-foreground">
                    {context.adminEmail}
                  </p>
                </div>
              </div>
              <SidebarMenu>
                <SidebarMenuItem className="mx-2 my-0.5">
                  <SidebarMenuButton asChild tooltip="Admin - Home">
                    <Link
                      to="/admin"
                      activeOptions={{
                        includeSearch: false,
                        exact: true,
                      }}
                      className="flex cursor-pointer items-center rounded-lg px-3 py-2 text-sm"
                      activeProps={{
                        className:
                          "bg-volume-mid-purple font-medium text-volume-text-purple",
                      }}
                    >
                      <ShieldAlert />
                      <span>Admin - Home</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem className="mx-2 my-0.5">
                  <SidebarMenuButton asChild tooltip="Payments">
                    <Link
                      to="/admin/payments"
                      activeOptions={{
                        includeSearch: false,
                      }}
                      className="flex cursor-pointer items-center rounded-lg px-3 py-2 text-sm"
                      activeProps={{
                        className:
                          "bg-volume-mid-purple font-medium text-volume-text-purple",
                      }}
                    >
                      <CreditCard />
                      <span>Payments</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem className="mx-2 my-0.5">
                  <SidebarMenuButton asChild tooltip="FX">
                    <Link
                      to="/admin/fx"
                      activeOptions={{
                        includeSearch: false,
                      }}
                      className="flex cursor-pointer items-center rounded-lg px-3 py-2 text-sm"
                      activeProps={{
                        className:
                          "bg-volume-mid-purple font-medium text-volume-text-purple",
                      }}
                    >
                      <TrendingUp />
                      <span>FX</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem className="mx-2 my-0.5">
                  <SidebarMenuButton asChild tooltip="Compliance">
                    <Link
                      to="/admin/compliance"
                      activeOptions={{
                        includeSearch: false,
                      }}
                      className="flex cursor-pointer items-center rounded-lg px-3 py-2 text-sm"
                      activeProps={{
                        className:
                          "bg-volume-mid-purple font-medium text-volume-text-purple",
                      }}
                    >
                      <FileCheck />
                      <span>Compliance</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarRail />
      </Sidebar>
      <main
        className="bg-volume-grey w-full overflow-y-auto"
        style={{
          scrollbarGutter: "stable",
          width:
            sidebarState === "collapsed"
              ? "max(calc(100% - var(--sidebar-width-icon)), 750px)"
              : "max(calc(100% - var(--sidebar-width)), 750px)",
          height: "100vh",
        }}
      >
        <div className="mt-2 mb-4 flex flex-1 flex-col pl-4 pr-4">
          <div className="flex items-center gap-2 mb-2 w-full">
            <SidebarTrigger />
            <button
              type="button"
              className="ml-auto flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              onClick={() => {
                void signOut(context.queryClient, router);
              }}
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
          <Outlet />
        </div>
      </main>
    </>
  );
}

function RouteComponent() {
  const context = Route.useRouteContext();

  return (
    <SidebarProvider>
      <SessionExpirationCheck decodedJwt={context.decodedJwt} />
      <AdminLayoutContent />
    </SidebarProvider>
  );
}
